import type { FilterSearchPropsMenu } from '@components';
import type { SelectedLeafInfo } from '@components/filter-checkbox-selector/helpers.ts';
import { msg } from '@lingui/core/macro';

const LEARNING = 'Learning';
const LEARNING_MY_TASKS = 'LearningMyTasks';
const LEARNING_TASK_EVALUATION = 'LearningTaskEvaluation';
const SYSTEM = 'System';
const NOTIFICATION_SOURCE_TYPE = 'NotificationSourceType';
const SOURCE_TYPE_SEPARATOR = ':';

const PATHWAY = { name: 'Pathway', displayName: msg`Pathway` };
const MY_TASKS = { name: 'My Tasks', displayName: msg`My Tasks` };
const TASK_TO_EVALUATE = { name: 'Task to evaluate', displayName: msg`Task to evaluate` };
const LEARNING_OPTION = { name: 'learning', displayName: msg`Learning` };
const SYSTEM_OPTION = { name: SYSTEM, displayName: msg`System` };

const ASSIGNED = { name: 'Assigned', displayName: msg`Assigned` };
const UNASSIGNED = { name: 'Unassigned', displayName: msg`Unassigned` };
const UNSUBSCRIBED = { name: 'Unsubscribed', displayName: msg`Unsubscribed` };
const CATEGORY_CHANGED = { name: 'Category changed', displayName: msg`Category changed` };
const DUE_DATE_CHANGED = { name: 'Due-date changed', displayName: msg`Due-date changed` };
const COMPLETED = { name: 'Completed', displayName: msg`Completed` };
const PASSED = { name: 'Passed', displayName: msg`Passed` };
const FAILED = { name: 'Failed', displayName: msg`Failed` };
const UPDATED = { name: 'Updated', displayName: msg`Updated` };
const OVERDUE = { name: 'Overdue', displayName: msg`Overdue` };
const DUE_SOON = { name: 'Due-soon', displayName: msg`Due-soon` };
const CLOSED = { name: 'Closed', displayName: msg`Closed` };
const REDO = { name: 'Redo', displayName: msg`Redo` };
const ASSESSOR_CHANGED = { name: 'Assessor changed', displayName: msg`Assessor changed` };
const EXEMPTED = { name: 'Exempted', displayName: msg`Exempted` };
const EXEMPTION_REJECTED = { name: 'Exemption rejected', displayName: msg`Exemption rejected` };
const AUTO_RECALLED_CREW_SIGNED_OFF = {
    name: 'Auto-recalled: Crew signed off',
    displayName: msg`Auto-recalled: Crew signed off`,
};
const RECEIVED_FOR_EVALUATION = { name: 'Received for evaluation', displayName: msg`Received for evaluation` };
const RECEIVED_FOR_EXEMPTION = { name: 'Received for exemption', displayName: msg`Received for exemption` };
const RECALLED_BY_ASSESSEE = { name: 'Recalled by Assessee', displayName: msg`Recalled by Assessee` };
const AUTO_RECALLED_TASK_UPDATED = {
    name: 'Auto-recalled: Task updated',
    displayName: msg`Auto-recalled: Task updated`,
};
const AUTO_RECALLED_TASK_REMOVED = {
    name: 'Auto-recalled: Task removed',
    displayName: msg`Auto-recalled: Task removed`,
};
const INFO = { name: 'Info', displayName: msg`Info` };
const SECURITY = { name: 'Security', displayName: msg`Security` };
const MAINTENANCE = { name: 'Maintenance', displayName: msg`Maintenance` };

const PATHWAY_UNASIGNED = 'PathwayUnasigned';
const PATHWAY_UNSUBSCRIBED = 'PathwayUnsubscribed';
const LEARNING_REASSIGNED_CATEGORY_CHANGED = 'LearningReassignedCategoryChanged';
const DUE_DATE_CHANGED_TYPE = 'DueDateChanged';
const UPDATED_TYPE = 'Updated';
const DUE_SOON_TYPE = 'DueSoon';
const TASK_CLOSED = 'TaskClosed';
const TASK_REDO = 'TaskRedo';
const TASK_ASSESSOR_CHANGED = 'TaskAssessorChanged';
const TASK_EXEMPTED = 'TaskExempted';
const TASK_EXEMPTION_REJECTED = 'TaskExemptionRejected';
const TASK_RECALLED_CREW_SIGN_OFF = 'TaskRecalledCrewSignOff';
const TASK_RECEIVED_FOR_EVALUATION = 'TaskReceivedForEvaluation';
const TASK_RECEIVED_FOR_EXEMPTION = 'TaskReceivedForExemption';
const TASK_RECALLED_BY_ASSESSEE = 'TaskRecalledByAssessee';
const TASK_RECALLED_ELEMENT_UPDATED = 'TaskRecalledElementUpdated';
const TASK_RECALLED_ELEMENT_DELETED = 'TaskRecalledElementDeleted';

const NOTIFICATION_SOURCES: Record<string, string> = {
    [PATHWAY.name]: LEARNING,
    [MY_TASKS.name]: LEARNING_MY_TASKS,
    [TASK_TO_EVALUATE.name]: LEARNING_TASK_EVALUATION,
    [SYSTEM_OPTION.name]: SYSTEM,
};

const NOTIFICATION_TYPES: Record<string, Record<string, string>> = {
    [PATHWAY.name]: {
        [ASSIGNED.name]: ASSIGNED.name,
        [UNASSIGNED.name]: PATHWAY_UNASIGNED,
        [UNSUBSCRIBED.name]: PATHWAY_UNSUBSCRIBED,
        [CATEGORY_CHANGED.name]: LEARNING_REASSIGNED_CATEGORY_CHANGED,
        [DUE_DATE_CHANGED.name]: DUE_DATE_CHANGED_TYPE,
        [COMPLETED.name]: COMPLETED.name,
        [PASSED.name]: PASSED.name,
        [FAILED.name]: FAILED.name,
        [UPDATED.name]: UPDATED_TYPE,
        [OVERDUE.name]: OVERDUE.name,
        [DUE_SOON.name]: DUE_SOON_TYPE,
    },
    [MY_TASKS.name]: {
        [CLOSED.name]: TASK_CLOSED,
        [REDO.name]: TASK_REDO,
        [ASSESSOR_CHANGED.name]: TASK_ASSESSOR_CHANGED,
        [EXEMPTED.name]: TASK_EXEMPTED,
        [EXEMPTION_REJECTED.name]: TASK_EXEMPTION_REJECTED,
        [AUTO_RECALLED_CREW_SIGNED_OFF.name]: TASK_RECALLED_CREW_SIGN_OFF,
    },
    [TASK_TO_EVALUATE.name]: {
        [RECEIVED_FOR_EVALUATION.name]: TASK_RECEIVED_FOR_EVALUATION,
        [RECEIVED_FOR_EXEMPTION.name]: TASK_RECEIVED_FOR_EXEMPTION,
        [RECALLED_BY_ASSESSEE.name]: TASK_RECALLED_BY_ASSESSEE,
        [AUTO_RECALLED_CREW_SIGNED_OFF.name]: TASK_RECALLED_CREW_SIGN_OFF,
        [AUTO_RECALLED_TASK_UPDATED.name]: TASK_RECALLED_ELEMENT_UPDATED,
        [AUTO_RECALLED_TASK_REMOVED.name]: TASK_RECALLED_ELEMENT_DELETED,
    },
    [SYSTEM_OPTION.name]: {
        [INFO.name]: INFO.name,
        [SECURITY.name]: SECURITY.name,
        [MAINTENANCE.name]: MAINTENANCE.name,
    },
};

export const getNotificationFilters = (selectedLeaves: SelectedLeafInfo[]): Record<string, string[]> => {
    const sourceTypes = new Set<string>();

    selectedLeaves.forEach(({ parent, child }) => {
        if (parent === null) return;

        const source = NOTIFICATION_SOURCES[parent];
        const type = NOTIFICATION_TYPES[parent]?.[child];

        if (source && type) {
            sourceTypes.add(`${source}${SOURCE_TYPE_SEPARATOR}${type}`);
        }
    });

    return { [NOTIFICATION_SOURCE_TYPE]: [...sourceTypes] };
};

const createOption = ({
    name,
    displayName,
}: Pick<FilterSearchPropsMenu, 'name' | 'displayName'>): FilterSearchPropsMenu => ({
    name,
    displayName,
    isSelected: false,
    submenu: [],
});

const PATHWAY_SUBMENU: FilterSearchPropsMenu[] = [
    createOption(ASSIGNED),
    createOption(UNASSIGNED),
    createOption(UNSUBSCRIBED),
    createOption(CATEGORY_CHANGED),
    createOption(DUE_DATE_CHANGED),
    createOption(COMPLETED),
    createOption(PASSED),
    createOption(FAILED),
    createOption(UPDATED),
    createOption(OVERDUE),
    createOption(DUE_SOON),
];

const MY_TASKS_SUBMENU: FilterSearchPropsMenu[] = [
    createOption(CLOSED),
    createOption(REDO),
    createOption(ASSESSOR_CHANGED),
    createOption(EXEMPTED),
    createOption(EXEMPTION_REJECTED),
    createOption(AUTO_RECALLED_CREW_SIGNED_OFF),
];

const TASK_TO_EVALUATE_SUBMENU: FilterSearchPropsMenu[] = [
    createOption(RECEIVED_FOR_EVALUATION),
    createOption(RECEIVED_FOR_EXEMPTION),
    createOption(RECALLED_BY_ASSESSEE),
    createOption(AUTO_RECALLED_CREW_SIGNED_OFF),
    createOption(AUTO_RECALLED_TASK_UPDATED),
    createOption(AUTO_RECALLED_TASK_REMOVED),
];

const LEARNING_FILTER: FilterSearchPropsMenu = {
    ...LEARNING_OPTION,
    isSelected: false,
    submenu: [
        { ...PATHWAY, isSelected: false, submenu: PATHWAY_SUBMENU },
        { ...MY_TASKS, isSelected: false, submenu: MY_TASKS_SUBMENU },
        { ...TASK_TO_EVALUATE, isSelected: false, submenu: TASK_TO_EVALUATE_SUBMENU },
    ],
};

const SYSTEM_FILTER: FilterSearchPropsMenu = {
    ...SYSTEM_OPTION,
    isSelected: false,
    submenu: [createOption(INFO), createOption(SECURITY), createOption(MAINTENANCE)],
};

export const NOTIFICATIONS_FILTER_MENU: FilterSearchPropsMenu[] = [LEARNING_FILTER, SYSTEM_FILTER];
